import type { IHighlight } from "@argument-studio/react-pdf-highlighter-with-categories";

/**
 * Pure comparison utilities for assessing a student's highlights against
 * an expert reference set. Used by the per-reading rubric/assessment flow
 * (Item 7 in the action plan).
 *
 * Stage 3.8 reworked this around "graduated completion credit." A student
 * highlight that nearly hits an expert annotation (e.g. 70% IoU at a 75%
 * threshold) earns partial credit instead of being lumped into a flat
 * "missed" bucket — that lets the completeness score scale smoothly with
 * selection quality.
 *
 * Geometric overlap is measured on each highlight's `position.boundingRect`
 * — the smallest rect enclosing all of its constituent text rects. This is
 * coarser than per-rect overlap but simpler and matches the level of
 * precision the demo needs.
 *
 * Page-awareness: two highlights on different pages cannot overlap.
 */

export interface CheckableHighlight {
  id: string;
  position: IHighlight["position"];
  comment: IHighlight["comment"];
}

/**
 * Intersection-over-union of the bounding rects of two highlights.
 * Returns 0 when the highlights are on different pages or fail to overlap.
 */
export function iou(a: CheckableHighlight, b: CheckableHighlight): number {
  if (a.position.pageNumber !== b.position.pageNumber) return 0;

  const ra = a.position.boundingRect;
  const rb = b.position.boundingRect;

  const aLeft = Math.min(ra.x1, ra.x2);
  const aRight = Math.max(ra.x1, ra.x2);
  const aTop = Math.min(ra.y1, ra.y2);
  const aBottom = Math.max(ra.y1, ra.y2);

  const bLeft = Math.min(rb.x1, rb.x2);
  const bRight = Math.max(rb.x1, rb.x2);
  const bTop = Math.min(rb.y1, rb.y2);
  const bBottom = Math.max(rb.y1, rb.y2);

  const interLeft = Math.max(aLeft, bLeft);
  const interRight = Math.min(aRight, bRight);
  const interTop = Math.max(aTop, bTop);
  const interBottom = Math.min(aBottom, bBottom);

  if (interRight <= interLeft || interBottom <= interTop) return 0;

  const interArea = (interRight - interLeft) * (interBottom - interTop);
  const aArea = (aRight - aLeft) * (aBottom - aTop);
  const bArea = (bRight - bLeft) * (bBottom - bTop);
  const unionArea = aArea + bArea - interArea;

  if (unionArea <= 0) return 0;
  return interArea / unionArea;
}

/** Convenience predicate — true iff IoU > 0. */
export function highlightsOverlap(
  a: CheckableHighlight,
  b: CheckableHighlight
): boolean {
  return iou(a, b) > 0;
}

/**
 * Per-expert assessment outcome.
 *
 * `credit` is the graduated completion score: 0 if the student didn't
 * overlap this expert at all, 1 if their best match was at or above the
 * threshold, and a linear interpolation in between
 * (`min(1, iou / threshold)`). Fractional credit is what lets the
 * completeness score distinguish "got close" from "didn't try."
 *
 * `categoriesAgree` is only meaningful when `credit === 1` — i.e. when
 * the student's selection passed the threshold. Below threshold the
 * student hasn't really identified the expert's item, so checking
 * their category isn't well-defined.
 */
export interface ExpertOutcome {
  expert: CheckableHighlight;
  bestMatch: { student: CheckableHighlight; iou: number } | null;
  credit: number;
  categoriesAgree?: boolean;
}

export interface AssessmentResult {
  outcomes: ExpertOutcome[];
  /** Student highlights with no greedy-paired expert. Informational. */
  extra: CheckableHighlight[];
  /** Σ credit, ∈ [0, outcomes.length]. */
  totalCredit: number;
  /** Number of outcomes with credit < 1 (partially or fully missed). */
  missedCount: number;
  /** Number of outcomes with credit == 1 (i.e. above-threshold match). */
  matchedCount: number;
  /** Among matched outcomes, count where the categories disagree. */
  miscategorizedCount: number;
  /**
   * Average IoU among matched (≥ threshold) pairs. NaN when there are
   * no matched pairs — callers should guard with isNaN before display.
   */
  averageMatchedIoU: number;
}

/**
 * Greedy one-to-one assignment of student → expert highlights by
 * descending IoU. Produces graduated completion credit per expert
 * (Stage 3.8 model). Threshold is in [0, 1] (i.e. fraction, not %).
 *
 * Strategy: rank all (expert, student) pairs by IoU descending, walk
 * the list claiming the highest-IoU unclaimed pair until none remain.
 * For each expert, store the IoU of its claimed pair (or 0 if none)
 * and derive credit from `min(1, iou / threshold)`. Unclaimed students
 * become `extra`.
 */
export function assessHighlights(
  studentHighlights: CheckableHighlight[],
  expertHighlights: CheckableHighlight[],
  threshold: number
): AssessmentResult {
  // Guard against threshold = 0 to avoid divide-by-zero in the credit
  // formula. A zero threshold would mean "any overlap counts as full
  // credit," which the (iou > 0 ? 1 : 0) interpretation captures.
  const safeThreshold = Math.max(threshold, 0.0001);

  const candidates: Array<{ s: number; e: number; iou: number }> = [];
  for (let e = 0; e < expertHighlights.length; e++) {
    const expertHl = expertHighlights[e];
    if (!expertHl) continue;
    for (let s = 0; s < studentHighlights.length; s++) {
      const studentHl = studentHighlights[s];
      if (!studentHl) continue;
      const score = iou(studentHl, expertHl);
      if (score > 0) candidates.push({ s, e, iou: score });
    }
  }
  candidates.sort((a, b) => b.iou - a.iou);

  const claimedStudent = new Set<number>();
  // expertIndex → its claimed pair (or undefined if unmatched)
  const expertPair: Array<{ s: number; iou: number } | undefined> = new Array(
    expertHighlights.length
  ).fill(undefined);

  for (const { s, e, iou: score } of candidates) {
    if (claimedStudent.has(s)) continue;
    if (expertPair[e] !== undefined) continue;
    claimedStudent.add(s);
    expertPair[e] = { s, iou: score };
  }

  const outcomes: ExpertOutcome[] = [];
  let totalCredit = 0;
  let matchedCount = 0;
  let miscategorizedCount = 0;
  let matchedIoUSum = 0;

  for (let e = 0; e < expertHighlights.length; e++) {
    const expert = expertHighlights[e];
    if (!expert) continue;
    const pair = expertPair[e];
    const score = pair?.iou ?? 0;
    const credit = Math.min(1, score / safeThreshold);
    totalCredit += credit;
    const bestMatch = pair
      ? { student: studentHighlights[pair.s] as CheckableHighlight, iou: score }
      : null;

    let categoriesAgree: boolean | undefined;
    if (credit === 1 && bestMatch) {
      matchedCount++;
      matchedIoUSum += score;
      categoriesAgree =
        bestMatch.student.comment.category === expert.comment.category;
      if (!categoriesAgree) miscategorizedCount++;
    }

    outcomes.push({ expert, bestMatch, credit, categoriesAgree });
  }

  const extra = studentHighlights.filter((_, s) => !claimedStudent.has(s));
  const missedCount = outcomes.filter((o) => o.credit < 1).length;
  const averageMatchedIoU =
    matchedCount > 0 ? matchedIoUSum / matchedCount : NaN;

  return {
    outcomes,
    extra,
    totalCredit,
    missedCount,
    matchedCount,
    miscategorizedCount,
    averageMatchedIoU,
  };
}
